import React, { useState, useRef, useEffect } from 'react';
import {
  Button, Form, Modal, Table, Alert,
} from 'react-bootstrap';
import { Navbar } from '../../../components/Navbar';
import { Sidebar } from '../../../components/Sidebar';
import useUnidadesConfiguracoes from '../../../services/api/unidadesConfiguracoes';
import ESTADOS_BRASILEIROS from '../../../utils/estadosBrasileiros';

const emptyForm = {
  nome: '', endereco: '', telefone: '', estado: '',
};

export default function UnidadesConfiguracoes() {
  const [submitting, setSubmitting] = useState(false);

  const normalizeMsg = (msg) => {
    if (!msg) return '';
    if (typeof msg === 'string') return msg;
    if (typeof msg?.message === 'string') return msg.message;
    if (typeof msg?.mensagem === 'string') return msg.mensagem;
    try { return JSON.stringify(msg); } catch { return String(msg); }
  };

  const [floatAlert, setFloatAlert] = useState({
    isVisible: false,
    message: '',
    variant: 'success',
  });

  const floatTimerRef = useRef(null);

  const showFloatAlert = (message, variant = 'success') => {
    if (floatTimerRef.current) clearTimeout(floatTimerRef.current);

    setFloatAlert({ isVisible: true, message: normalizeMsg(message), variant });
    floatTimerRef.current = setTimeout(() => {
      setFloatAlert({ isVisible: false, message: '', variant: 'success' });
      floatTimerRef.current = null;
    }, 3000);
  };

  useEffect(() => () => {
    if (floatTimerRef.current) clearTimeout(floatTimerRef.current);
  }, []);

  const [show, setShow] = useState(false);
  const [mode, setMode] = useState('create'); // create | edit
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const {
    unidades,
    isLoading,
    isError,
    criarUnidade,
    editarUnidade,
  } = useUnidadesConfiguracoes();

  const isBusy = submitting || isLoading;

  const openCreate = () => {
    setMode('create');
    setEditingId(null);
    setForm(emptyForm);
    setShow(true);
  };

  const openEdit = (u) => {
    setMode('edit');
    setEditingId(u.id);
    setForm({
      nome: u.nome || '',
      endereco: u.endereco || '',
      telefone: u.telefone || '',
      estado: u.estado || '',
    });
    setShow(true);
  };

  const validate = () => {
    if (!form.nome.trim()) return 'Informe o nome.';
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) {
      showFloatAlert(err, 'danger');
      return;
    }

    const payload = {
      nome: form.nome.trim(),
      endereco: form.endereco?.trim() || null,
      telefone: form.telefone?.trim() || null,
      estado: form.estado || null,
    };

    setSubmitting(true);
    try {
      if (mode === 'create') {
        await criarUnidade(payload);
      } else {
        await editarUnidade(editingId, payload);
      }

      setShow(false);
      setEditingId(null);
      setForm(emptyForm);
      showFloatAlert('Dados salvos com sucesso!', 'success');
    } catch (e) {
      console.error('Erro ao salvar unidade:', e);

      const status = e?.response?.status;
      const apiMsg = e?.response?.data?.mensagem
        || e?.response?.data?.message
        || e?.response?.data?.error;

      const msg = apiMsg
        || (status ? `Falha ao salvar (HTTP ${status}).` : null)
        || e?.message
        || 'Não foi possível salvar a unidade.';

      showFloatAlert(msg, 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const renderTbody = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={5} className="text-center text-muted py-4">
            Carregando...
          </td>
        </tr>
      );
    }

    if (isError) {
      return (
        <tr>
          <td colSpan={5} className="text-center text-danger py-4">
            Erro ao carregar unidades.
          </td>
        </tr>
      );
    }

    if ((unidades || []).length === 0) {
      return (
        <tr>
          <td colSpan={5} className="text-center text-muted py-4">
            Nenhuma unidade cadastrada.
          </td>
        </tr>
      );
    }

    return unidades.map((u) => (
      <tr key={u.id}>
        <td>{u.nome}</td>
        <td className="text-muted">{u.endereco || '-'}</td>
        <td>{u.telefone || '-'}</td>
        <td className="text-center">{u.estado || '—'}</td>

        <td className="text-center">
          <div className="d-flex justify-content-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => openEdit(u)}
              disabled={isBusy}
            >
              Editar
            </Button>
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <>
      {floatAlert.isVisible && (
      <div className="fixed-top mt-5" style={{ zIndex: 3000 }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-6">
              <Alert
                variant={floatAlert.variant}
                onClose={() => setFloatAlert({ isVisible: false, message: '', variant: 'success' })}
                dismissible
              >
                <p className="text-center mt-3">{floatAlert.message}</p>
              </Alert>
            </div>
          </div>
        </div>
      </div>
      )}

      <div id="wrapper">
        <Sidebar />

        <div id="content-wrapper" className="d-flex flex-column">
          <div id="content">
            <Navbar />

            <div className="container-fluid">
              <div className="d-sm-flex align-items-center justify-content-between mb-4">
                <h1 className="h3 mb-0 text-gray-800">Unidades - Configurações</h1>

                <Button variant="success" onClick={openCreate} disabled={isBusy}>
                  Criar nova unidade
                </Button>
              </div>

              <hr />

              <div className="card shadow mb-4">
                <div className="card-header py-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <strong className="m-0">Lista de unidades</strong>
                </div>

                <div className="card-body">
                  <Table responsive size="sm" className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: '24%' }}>Nome</th>
                        <th>Endereço</th>
                        <th style={{ width: '14%' }}>Telefone</th>
                        <th style={{ width: '10%' }} className="text-center">UF</th>
                        <th style={{ width: '14%' }} className="text-center">Ações</th>
                      </tr>
                    </thead>

                    <tbody>
                      {renderTbody()}
                    </tbody>
                  </Table>
                </div>
              </div>

              <Modal
                show={show}
                onHide={() => {
                  if (isBusy) return;
                  setShow(false);
                  setEditingId(null);
                  setForm(emptyForm);
                }}
                centered
              >
                <Modal.Header closeButton={!isBusy}>
                  <Modal.Title>
                    {mode === 'create' ? 'Criar nova unidade' : 'Editar unidade'}
                  </Modal.Title>
                </Modal.Header>

                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (isBusy) return;
                    save();
                  }}
                >
                  <Modal.Body>
                    <div className="mb-3">
                      <Form.Label className="fw-semibold">Nome:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Ex: São Paulo"
                        value={form.nome}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          nome: e.target.value,
                        }))}
                        maxLength={255}
                        disabled={isBusy}
                      />
                    </div>

                    <div className="mb-3">
                      <Form.Label className="fw-semibold">Endereço:</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={form.endereco}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          endereco: e.target.value,
                        }))}
                        maxLength={255}
                        disabled={isBusy}
                      />
                    </div>

                    <div className="mb-3">
                      <Form.Label className="fw-semibold">Telefone:</Form.Label>
                      <Form.Control
                        type="text"
                        value={form.telefone}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          telefone: e.target.value,
                        }))}
                        maxLength={255}
                        disabled={isBusy}
                      />
                    </div>

                    <div className="mb-3">
                      <Form.Label className="fw-semibold">UF:</Form.Label>
                      <Form.Select
                        value={form.estado}
                        onChange={(e) => setForm((prev) => ({
                          ...prev,
                          estado: e.target.value,
                        }))}
                        disabled={isBusy}
                      >
                        <option value="">Não informado</option>
                        {ESTADOS_BRASILEIROS.map((uf) => (
                          <option key={uf} value={uf}>{uf}</option>
                        ))}
                      </Form.Select>
                    </div>
                  </Modal.Body>

                  <Modal.Footer>
                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={() => {
                        if (isBusy) return;
                        setShow(false);
                        setEditingId(null);
                        setForm(emptyForm);
                      }}
                      disabled={isBusy}
                    >
                      Cancelar
                    </Button>

                    <Button variant="primary" type="submit" disabled={isBusy}>
                      {submitting ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </Modal.Footer>
                </Form>
              </Modal>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
